import StatusBox, {
  StatusKeyMapItem,
  statusMap,
} from 'components/StatusCountPanel/components/StatusBox/StatusBox';
import { useTranslation } from 'react-i18next';
import './CasesSummaryPanel.scss';
import { CaseListItem } from 'queries/cases/types';
import StageSummaryPanel from './StageSummaryPanel';
import PieChartOutlinedIcon from '@mui/icons-material/PieChartOutlined';
import { useFeatureFlag } from '@shape/feature-flag';
import { useCaseContext } from '../../Cases.context';
import { TextButton } from '@shape-ds/components';
import { pageEnum, Severity, StatusCountPanelContainer } from 'components';
import { MULTIVALUE_MODE } from 'hooks/useFilters/useFilters';

export const severityMap: { [key: string]: SeverityNumber } = {
  low: 1,
  medium: 2,
  high: 3,
  very_high: 4,
};

export type StatusKeys = keyof Pick<CaseListItem, 'status' | 'action_status' | 'severity'>;
type StatusData = {
  [key in StatusKeys]?: string;
};

type Props = {
  counterKeys: StatusKeys[];
  shouldShowCompletedActionStatus?: boolean;
};

const actionStatusMap = {
  on_track: 'on_track',
  delayed: 'delayed',
  executed: 'executed',
  none: 'none',
};

export const statusKeyMap: { [key: string]: StatusKeyMapItem } = {
  status: statusMap,
  action_status: actionStatusMap,
};

const CasesSummaryPanel = ({ counterKeys, shouldShowCompletedActionStatus }: Props) => {
  const { t } = useTranslation();

  const context = useCaseContext();

  const {
    filteredData,
    allActiveFilters,
    dataToCount,
    onChangeFilters,
    selectedCase,
    setshowAnalytics,
  } = context;

  const stageDataToCount = dataToCount('status') as Partial<CaseListItem>[];
  const page = 'case';

  const isCasesExpandEnabled = useFeatureFlag('cases-expand');
  const showAnalyticsButton = isCasesExpandEnabled && !selectedCase;

  return (
    <StatusCountPanelContainer>
      <StatusBox data={(filteredData as StatusData[]) ?? []} statusKey="total" page={page} />

      <Severity page={pageEnum.cases} context={context} />

      <StageSummaryPanel
        stageDataToCount={stageDataToCount}
        activeItems={allActiveFilters['status']}
        onChange={(selectedValue: string) =>
          onChangeFilters({
            filterName: 'status',
            filterValue: [selectedValue],
            multivalueMode: MULTIVALUE_MODE.MERGE_WITH_PREVIOUS,
          })
        }
      />

      {counterKeys.map((statusKey) => (
        <StatusBox
          data={dataToCount(statusKey) as StatusData[]}
          statusKey={statusKey}
          statusKeyMap={statusKeyMap}
          key={statusKey}
          page={page}
          activeItems={allActiveFilters[statusKey]}
          onChange={(selectedValue: string) =>
            onChangeFilters({
              filterName: statusKey,
              filterValue: [selectedValue],
              multivalueMode: MULTIVALUE_MODE.MERGE_WITH_PREVIOUS,
            })
          }
          shouldShowCompletedActionStatus={shouldShowCompletedActionStatus}
        />
      ))}

      {showAnalyticsButton && (
        <TextButton
          className="cases-summary-panel-expand-button"
          startIcon={<PieChartOutlinedIcon />}
          onClick={() => setshowAnalytics(false)}
        >
          {t('case:show_analytics')}
        </TextButton>
      )}
    </StatusCountPanelContainer>
  );
};

export default CasesSummaryPanel;
